import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Chart from "chart.js/auto";
import {
  collection,
  getDocs,
  query as firestoreQuery,
  where,
} from "firebase/firestore";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { db } from "../firebase";
import "./PopuliReportPage.css";

const BOT_NAME = "YiviBot";
const POPULI_USERS_COLLECTION = "populi-datas";
const POPULI_TALKS_COLLECTION = "populi-talks";
const POPULI_DOCUMENTS_COLLECTION = "populi-documents";
const ROLE_COLORS = ["#347fb1", "#d99a18", "#27946d", "#805ad5", "#d54949", "#2b6cb0"];

function Icon({ name }) {
  const paths = {
    refresh: <path d="M20 11a8 8 0 1 0-2.34 5.66M20 4v7h-7" />,
    report: <path d="M4 19V9m5 10V5m5 14v-7m5 7V3" />,
    excel: <path d="M14 3h5v18H5V3h5m-2 5 5 8m0-8-5 8" />,
    word: <path d="M4 5h16v14H4V5Zm3 4 2 6 3-6 3 6 2-6" />,
    close: <path d="m6 6 12 12M18 6 6 18" />,
    users: <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2m7-10a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm13 10v-2a4 4 0 0 0-3-3.87" />,
    message: <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4v8Z" />,
    chart: <path d="M4 20V10m6 10V4m6 16v-7m4 7H2" />,
    calendar: <path d="M4 5h16v16H4V5Zm4-3v6m8-6v6M4 10h16" />,
    document: <path d="M6 3h8l4 4v14H6V3Zm8 0v5h5M9 12h6M9 16h6" />,
  };
  return <svg className="report-icon" viewBox="0 0 24 24" aria-hidden="true">{paths[name]}</svg>;
}

function toDate(value) {
  if (value?.toDate instanceof Function) return value.toDate();
  if (value instanceof Date) return value;
  if (!value) return null;

  const normalized = typeof value === "string"
    ? value.replace(" ", "T")
    : value;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

function latestProfilesByTelegramId(users) {
  return users.reduce((profiles, user) => {
    const telegramId = String(user.telegram_id ?? "");
    if (!telegramId) return profiles;

    const currentVersion = Number(user.version) || 0;
    const existingVersion = Number(profiles.get(telegramId)?.version) || 0;
    if (!profiles.has(telegramId) || currentVersion >= existingVersion) {
      profiles.set(telegramId, user);
    }
    return profiles;
  }, new Map());
}

function buildDashboardData(users, talks, documentRows) {
  const now = new Date();
  const todayStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const tomorrowStart = new Date(todayStart);
  tomorrowStart.setUTCDate(tomorrowStart.getUTCDate() + 1);
  const sevenDaysAgo = new Date(todayStart);
  sevenDaysAgo.setUTCDate(sevenDaysAgo.getUTCDate() - 7);

  const activeUserIds = new Set(
    users
      .filter((user) => String(user.step) === "2")
      .map((user) => String(user.telegram_id ?? ""))
      .filter(Boolean),
  );
  const todaysUserIds = new Set();
  const recentTalks = [];
  const talkCountsByDay = {};

  talks.forEach((talk) => {
    const requestDate = toDate(talk.request_time);
    if (!requestDate) return;
    if (requestDate >= todayStart && requestDate < tomorrowStart && talk.telegram_id != null) {
      todaysUserIds.add(String(talk.telegram_id));
    }
    if (requestDate < sevenDaysAgo) return;

    recentTalks.push(talk);
    const day = requestDate.toISOString().slice(0, 10);
    talkCountsByDay[day] = (talkCountsByDay[day] || 0) + 1;
  });

  const documents = new Map();
  const documentsV2 = new Map();
  documentRows.forEach((document) => {
    if (!document.documentID) return;
    documents.set(document.documentID, document.documentName);
    if (Number(document.version) === 2) {
      documentsV2.set(document.documentID, document.documentName);
    }
  });

  const documentUsageCounts = {};
  recentTalks.forEach((talk) => {
    const documentName = documents.get(talk.matched_document);
    if (documentName) {
      documentUsageCounts[documentName] = (documentUsageCounts[documentName] || 0) + 1;
    }
  });

  const profiles = latestProfilesByTelegramId(users);
  const reportSourceHistory = [...talks]
    .sort((left, right) => {
      const leftTime = toDate(left.request_time)?.getTime() ?? -Infinity;
      const rightTime = toDate(right.request_time)?.getTime() ?? -Infinity;
      return rightTime - leftTime;
    })
    .map((talk) => {
      const profile = profiles.get(String(talk.telegram_id ?? "")) || {};
      const documentName = talk.matched_document === "doc_5555555"
        ? "IT Sorusu"
        : documentsV2.get(talk.matched_document) || talk.matched_document;
      return {
        userQuestion: talk.user,
        aiAnswer: talk.ai_response,
        document: documentName,
        askTime: toDate(talk.request_time) || talk.request_time,
        userRole: profile.role,
        userName: profile.userName,
      };
    });

  return {
    totalUserCount: activeUserIds.size,
    userPercentage: todaysUserIds.size,
    documentUsage: Object.entries(documentUsageCounts)
      .sort((left, right) => right[1] - left[1])
      .slice(0, 10)
      .map(([document, usage]) => ({ document, usage })),
    dailyTalks: Object.keys(talkCountsByDay)
      .sort()
      .map((talkDate) => ({ talkDate, talkCount: talkCountsByDay[talkDate] })),
    populiHistory: reportSourceHistory.slice(0, 500),
    reportSourceHistory,
  };
}

function localDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString("tr-TR");
}

function createRoleChartData(history) {
  const dailyRoleCount = {};
  history.forEach((item) => {
    const date = new Date(item.askTime);
    if (Number.isNaN(date.getTime())) return;
    const dateKey = date.toISOString().slice(0, 10);
    const role = item.userRole || "NoRole";
    dailyRoleCount[dateKey] ||= {};
    dailyRoleCount[dateKey][role] = (dailyRoleCount[dateKey][role] || 0) + 1;
  });

  const labels = Object.keys(dailyRoleCount).sort();
  const roles = [...new Set(Object.values(dailyRoleCount).flatMap((item) => Object.keys(item)))];
  return {
    labels,
    datasets: roles.map((role, index) => ({
      label: role,
      data: labels.map((date) => dailyRoleCount[date][role] || 0),
      borderColor: ROLE_COLORS[index % ROLE_COLORS.length],
      backgroundColor: `${ROLE_COLORS[index % ROLE_COLORS.length]}22`,
      fill: false,
      tension: 0.25,
    })),
  };
}

function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function ReportModal({ report, roleCanvasRef, onExport, onClose }) {
  return (
    <div className="report-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="report-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Rapor sonuçları"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="report-modal__header">
          <div>
            <h2>Rapor Sonuçları</h2>
            <p>
              {report.startDate || "Başlangıç"} – {report.finishDate || "Bitiş"}
              <strong> · Toplam {report.history.length} soru</strong>
            </p>
          </div>
          <button className="report-icon-button" type="button" onClick={onClose} aria-label="Kapat">
            <Icon name="close" />
          </button>
        </header>

        <div className="report-chart-card">
          <h3>Seçilen Aralıktaki Konuşmalar</h3>
          <div className="report-table-wrap">
            <table className="report-table">
              <thead>
                <tr><th>Soru</th><th>Populi Cevap</th><th>Kaynak Doküman</th><th>Rol</th><th>Zaman</th></tr>
              </thead>
              <tbody>
                {!report.history.length && (
                  <tr><td colSpan="5" className="report-table__empty">Bu tarih aralığında kayıt bulunmuyor.</td></tr>
                )}
                {report.history.map((row, index) => (
                  <tr key={`${row.askTime || "row"}-${index}`}>
                    <td>{row.userQuestion || "—"}</td>
                    <td>{row.aiAnswer || "—"}</td>
                    <td>{row.document || "—"}</td>
                    <td>{row.userRole || "—"}</td>
                    <td>{localDate(row.askTime)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="report-chart-card">
          <h3>Günlük Soru Dağılımı</h3>
          <div className="report-chart"><canvas ref={roleCanvasRef} /></div>
        </div>

        <footer className="report-modal__actions">
          <button className="report-button report-button--ghost" type="button" onClick={onClose}>Kapat</button>
          <button className="report-button report-button--success" type="button" onClick={onExport}>
            <Icon name="word" /> Raporu Aktar
          </button>
        </footer>
      </section>
    </div>
  );
}

export default function PopuliReportPage({ userEmail: emailProp, onUnauthorized }) {
  const [dashboard, setDashboard] = useState({
    totalUserCount: 0,
    userPercentage: 0,
    documentUsage: [],
    dailyTalks: [],
    populiHistory: [],
  });
  const [startDate, setStartDate] = useState("");
  const [finishDate, setFinishDate] = useState("");
  const [report, setReport] = useState(null);
  const [reportSourceHistory, setReportSourceHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const documentsCanvasRef = useRef(null);
  const conversationsCanvasRef = useRef(null);
  const roleCanvasRef = useRef(null);
  const documentsChartRef = useRef(null);
  const conversationsChartRef = useRef(null);
  const roleChartRef = useRef(null);

  const userEmail =
    emailProp || (typeof window !== "undefined" ? sessionStorage.getItem("userEmail") : "") || "";

  const loadDashboard = useCallback(async () => {
    if (!userEmail) return;
    setLoading(true);
    setError("");
    try {
      const [usersSnapshot, talksSnapshot, documentsSnapshot] = await Promise.all([
        getDocs(firestoreQuery(
          collection(db, POPULI_USERS_COLLECTION),
          where("botname", "==", BOT_NAME),
        )),
        getDocs(firestoreQuery(
          collection(db, POPULI_TALKS_COLLECTION),
          where("botname", "==", BOT_NAME),
        )),
        getDocs(collection(db, POPULI_DOCUMENTS_COLLECTION)),
      ]);
      const dashboardData = buildDashboardData(
        usersSnapshot.docs.map((item) => item.data()),
        talksSnapshot.docs.map((item) => item.data()),
        documentsSnapshot.docs.map((item) => item.data()),
      );
      setReportSourceHistory(dashboardData.reportSourceHistory);
      setDashboard({
        totalUserCount: dashboardData.totalUserCount,
        userPercentage: dashboardData.userPercentage,
        documentUsage: dashboardData.documentUsage,
        dailyTalks: dashboardData.dailyTalks,
        populiHistory: dashboardData.populiHistory,
      });
    } catch (requestError) {
      setError(requestError.message || "Dashboard verileri Firestore'dan yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }, [userEmail]);

  useEffect(() => {
    if (!userEmail) {
      onUnauthorized?.();
      return;
    }
    loadDashboard();
  }, [loadDashboard, onUnauthorized, userEmail]);

  useEffect(() => {
    if (!documentsCanvasRef.current || !conversationsCanvasRef.current) return undefined;
    documentsChartRef.current?.destroy();
    conversationsChartRef.current?.destroy();

    documentsChartRef.current = new Chart(documentsCanvasRef.current, {
      type: "bar",
      data: {
        labels: dashboard.documentUsage.map((item) => item.document),
        datasets: [{
          label: "Erişim Sayısı",
          data: dashboard.documentUsage.map((item) => item.usage),
          backgroundColor: "rgba(52, 127, 177, .65)",
          borderColor: "#347fb1",
          borderWidth: 1,
          borderRadius: 5,
        }],
      },
      options: { responsive: true, maintainAspectRatio: false, scales: { y: { beginAtZero: true } } },
    });

    conversationsChartRef.current = new Chart(conversationsCanvasRef.current, {
      type: "line",
      data: {
        labels: dashboard.dailyTalks.map((item) => item.talkDate),
        datasets: [{
          label: "Soru Sayısı",
          data: dashboard.dailyTalks.map((item) => item.talkCount),
          backgroundColor: "rgba(39, 148, 109, .14)",
          borderColor: "#27946d",
          fill: true,
          tension: 0.35,
        }],
      },
      options: { responsive: true, maintainAspectRatio: false, scales: { y: { beginAtZero: true } } },
    });

    return () => {
      documentsChartRef.current?.destroy();
      conversationsChartRef.current?.destroy();
    };
  }, [dashboard.dailyTalks, dashboard.documentUsage]);

  useEffect(() => {
    if (!report || !roleCanvasRef.current) return undefined;
    roleChartRef.current?.destroy();
    roleChartRef.current = new Chart(roleCanvasRef.current, {
      type: "line",
      data: createRoleChartData(report.history),
      options: { responsive: true, maintainAspectRatio: false, scales: { y: { beginAtZero: true } } },
    });

    return () => {
      roleChartRef.current?.destroy();
    };
  }, [report]);

  const talkPercentage = useMemo(() => {
    if (!dashboard.totalUserCount) return "0.00";
    return ((dashboard.userPercentage / dashboard.totalUserCount) * 100).toFixed(2);
  }, [dashboard.totalUserCount, dashboard.userPercentage]);

  function loadReport() {
    if (!startDate || !finishDate) {
      setError("Rapor için başlangıç ve bitiş tarihlerini seçmelisiniz.");
      return;
    }
    if (startDate > finishDate) {
      setError("Başlangıç tarihi bitiş tarihinden sonra olamaz.");
      return;
    }
    setError("");
    const rangeStart = new Date(`${startDate}T00:00:00`);
    const rangeEnd = new Date(`${finishDate}T00:00:00`);
    rangeEnd.setDate(rangeEnd.getDate() + 1);
    setReport({
      history: reportSourceHistory.filter((row) => {
        const askTime = toDate(row.askTime);
        return askTime && askTime >= rangeStart && askTime < rangeEnd;
      }),
      startDate,
      finishDate,
    });
  }

  function exportExcel() {
    const rows = dashboard.populiHistory.map((row) => ({
      "Kullanıcı Sorusu": row.userQuestion,
      "Populi Cevap": row.aiAnswer,
      "Kaynak Doküman": row.document,
      "Kullanıcı Rolü": row.userRole,
      "Soru Zamanı": localDate(row.askTime),
    }));
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Populi History");
    XLSX.writeFile(workbook, "PopuliHistory.xlsx");
  }

  async function exportWord() {
    if (!report || !roleCanvasRef.current) return;
    setLoading(true);
    try {
      const roleChart = roleCanvasRef.current.toDataURL("image/png");
      const resultRows = report.history.map((row) => `
        <tr>
          <td>${escapeHtml(row.userQuestion || "—")}</td>
          <td>${escapeHtml(row.aiAnswer || "—")}</td>
          <td>${escapeHtml(row.document || "—")}</td>
          <td>${escapeHtml(row.userRole || "—")}</td>
          <td>${escapeHtml(localDate(row.askTime))}</td>
        </tr>`).join("");
      const reportHtml = `<!doctype html>
        <html><head><meta charset="utf-8"><title>Populi Rapor</title></head>
        <body>
          <h1>Populi Rapor</h1>
          <h2>${report.startDate || "Başlangıç"} – ${report.finishDate || "Bitiş"} · Toplam ${report.history.length} soru</h2>
          <h2>Günlük Soru Dağılımı</h2><p><img src="${roleChart}" width="600" alt="Günlük soru grafiği"></p>
          <h2>Konuşmalar</h2>
          <table border="1" cellspacing="0" cellpadding="6">
            <thead><tr><th>Soru</th><th>Populi Cevap</th><th>Kaynak Doküman</th><th>Rol</th><th>Zaman</th></tr></thead>
            <tbody>${resultRows}</tbody>
          </table>
        </body></html>`;
      saveAs(new Blob([reportHtml], { type: "application/msword;charset=utf-8" }), "PopuliRapor.doc");
    } catch (exportError) {
      setError(exportError.message || "Word raporu oluşturulamadı.");
    } finally {
      setLoading(false);
    }
  }

  if (!userEmail) {
    return <div className="report-page report-empty">Bu sayfayı görüntülemek için giriş yapmalısınız.</div>;
  }

  return (
    <main className="report-page">
      <section className="report-shell">
        <header className="report-page-header">
          <div>
            <span className="report-eyebrow">Analitik</span>
            <h1>Raporlar</h1>
            <p>Kullanım eğilimlerini takip edin, en çok başvurulan dokümanları görün ve seçtiğiniz tarih aralığı için ayrıntılı rapor oluşturun.</p>
          </div>
        </header>

        <div className="report-toolbar">
          <div className="report-toolbar__title">
            <span><Icon name="calendar" /></span>
            <div><strong>Rapor aralığı</strong><small>Başlangıç ve bitiş tarihini seçin</small></div>
          </div>
          <div className="report-filters">
            <label>Başlangıç Tarihi<input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} /></label>
            <label>Bitiş Tarihi<input type="date" value={finishDate} onChange={(event) => setFinishDate(event.target.value)} /></label>
            <button className="report-button report-button--success" onClick={loadReport} disabled={loading}>
              <Icon name="report" /> Rapor Oluştur
            </button>
            <button className="report-button report-button--secondary" onClick={loadDashboard} disabled={loading}>
              <Icon name="refresh" /> Yenile
            </button>
          </div>
        </div>

        {error && <div className="report-alert" role="alert">{error}</div>}

        <div className="report-stats">
          <article className="report-stat report-stat--blue">
            <span className="report-stat__icon"><Icon name="users" /></span>
            <div><h2>Toplam Kullanıcı Sayısı</h2><strong>{dashboard.totalUserCount}</strong><p>Aktif kullanıcı profili</p></div>
          </article>
          <article className="report-stat report-stat--slate">
            <span className="report-stat__icon"><Icon name="message" /></span>
            <div><h2>Bugünkü Konuşma</h2><strong>{talkPercentage}%</strong><p>Aktif kullanıcılara oranı</p></div>
          </article>
        </div>

        <div className="report-chart-grid">
          <section className="report-card">
            <div className="report-card__intro"><span><Icon name="document" /></span><div><h2>En Çok Başvurulan Dokümanlar</h2><p>Son 7 gündeki ilk 10 kaynak</p></div></div>
            <div className="report-chart"><canvas ref={documentsCanvasRef} /></div>
          </section>

          <section className="report-card">
            <div className="report-card__intro"><span><Icon name="chart" /></span><div><h2>Konuşma Trendi</h2><p>Son 7 günlük soru dağılımı</p></div></div>
            <div className="report-chart"><canvas ref={conversationsCanvasRef} /></div>
          </section>
        </div>

        <section className="report-card">
          <div className="report-card__header">
            <div><h2>Populi Cevaplar (Son 50 Konuşma)</h2><p>En güncel kullanıcı soruları ve yanıtları</p></div>
            <button className="report-button report-button--excel" onClick={exportExcel} disabled={!dashboard.populiHistory.length}>
              <Icon name="excel" /> Excel’e Aktar
            </button>
          </div>
          <div className="report-table-wrap">
            <table className="report-table">
              <thead><tr><th>Soru</th><th>Populi Cevap</th><th>Kaynak Doküman</th><th>Zaman</th></tr></thead>
              <tbody>
                {!dashboard.populiHistory.length && <tr><td colSpan="4" className="report-table__empty">Kayıt bulunmuyor.</td></tr>}
                {dashboard.populiHistory.slice(0, 50).map((row, index) => (
                  <tr key={`${row.askTime || "row"}-${index}`}>
                    <td data-label="Soru">{row.userQuestion || "—"}</td>
                    <td data-label="Populi Cevap">{row.aiAnswer || "—"}</td>
                    <td data-label="Kaynak Doküman"><span className="report-source">{row.document || "—"}</span></td>
                    <td data-label="Zaman">{localDate(row.askTime)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </section>

      {report && (
        <ReportModal
          report={report}
          roleCanvasRef={roleCanvasRef}
          onExport={exportWord}
          onClose={() => setReport(null)}
        />
      )}

      {loading && <div className="report-loading" role="status"><span /> İşlem yapılıyor…</div>}
    </main>
  );
}
